import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, Component, ViewEncapsulation, Input, Output, ViewChild, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
const _c0 = ["inputElementRef"];
function TextAreaComponent_span_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 4)(1, "em");
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
    i0.ɵɵtext(3, " / ");
    i0.ɵɵelementStart(4, "em");
    i0.ɵɵtext(5);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(ctx_r1.modelValue.length);
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate(ctx_r1.maxlength);
  }
}
const _c1 = (a0, a1) => ({
  "readonly": a0,
  "not-resizable": a1
});
class TextAreaComponent {
  constructor(renderer) {
    this.renderer = renderer;
    this.modelValue = '';
    /**
     * Accessibility field aria-labelledby see: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-labelledby_attribute
     */
    this.accessLabelledby = '';
    /**
     * Accessibility field aria-label see: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-label_attribute
     */
    this.accessLabel = '';
    /**
     * placeholder to assigned to the underlying input element.
     */
    this.placeholder = '';
    /**
     * Shows the input element in read only state
     */
    this.readonly = false;
    /**
     * Determines whether the input field is required or not
     */
    this.required = false;
    /**
     * Specifies if the text area is resizable or not.  Defalults to false
     */
    this.resizable = false;
    /**
     * Specifies the maximum number of characters allowed in the text area
     */
    this.maxlength = 0;
    /**
     * wrap style to assigned to the underlying input element. If no wrap is given, it will default to soft
     */
    this.wrap = 'soft';
    /**
     * specifies the visible width of a text area
     */
    this.cols = 0;
    /**
     * specifies the visible number of lines in a text area
     */
    this.rows = 0;
    /**
     * should input support autocomplete
     */
    this.autocomplete = 'off';
    /**
     * callback which is fired whenever a focus event occurs on the input field
     */
    this.focusEvent = new EventEmitter();
    /**
     * The registered callback function called when a change event occurs on the input element.
     */
    this.onChangedCallback = _ => {}; // eslint-disable-line @typescript-eslint/no-unused-vars
    /**
     * The registered callback function called when a blur event occurs on the input element.
     */
    this.onTouchedCallback = () => {};
  }
  ngOnInit() {
    this.setupAccessibilityAttributes();
  }
  /**
   * Registers a function called when the control value changes.
   */
  registerOnChange(fn) {
    this.onChangedCallback = fn;
  }
  onChanged(event) {
    this.modelValue = event.target.value;
    this.onChangedCallback(this.modelValue);
  }
  /**
   * Registers a function called when the control is touched.
   */
  registerOnTouched(fn) {
    this.onTouchedCallback = fn;
  }
  onTouched() {
    this.onTouchedCallback();
  }
  /**
   * Write a value to the html element
   */
  writeValue(obj) {
    if (obj === undefined || obj === null) {
      this.modelValue = '';
    } else {
      this.modelValue = obj;
    }
  }
  /**
   * Invoked when the disabled attribute is updated
   */
  setDisabledState(isDisabled) {
    this.renderer.setProperty(this.inputElementRef.nativeElement, 'disabled', isDisabled);
    this.inputElementRef.nativeElement.setAttribute('aria-disabled', isDisabled);
  }
  setupAccessibilityAttributes() {
    if (this.readonly) {
      this.inputElementRef.nativeElement.setAttribute('readonly', 'true');
      this.inputElementRef.nativeElement.setAttribute('aria-readonly', 'true');
    }
    if (this.required) {
      this.inputElementRef.nativeElement.setAttribute('required', 'true');
      this.inputElementRef.nativeElement.setAttribute('aria-required', 'true');
    }
    if (this.accessLabelledby) {
      this.inputElementRef.nativeElement.setAttribute('aria-labelledby', this.accessLabelledby);
    } else {
      this.inputElementRef.nativeElement.setAttribute('aria-label', this.accessLabel);
    }
  }
  /** @nocollapse */
  static {
    this.ɵfac = function TextAreaComponent_Factory(t) {
      return new (t || TextAreaComponent)(i0.ɵɵdirectiveInject(i0.Renderer2));
    };
  }
  /** @nocollapse */
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: TextAreaComponent,
      selectors: [["cxone-text-area"]],
      viewQuery: function TextAreaComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c0, 7);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.inputElementRef = _t.first);
        }
      },
      inputs: {
        name: "name",
        accessLabelledby: "accessLabelledby",
        accessLabel: "accessLabel",
        placeholder: "placeholder",
        readonly: "readonly",
        required: "required",
        textInputId: "textInputId",
        resizable: "resizable",
        maxlength: "maxlength",
        wrap: "wrap",
        cols: "cols",
        rows: "rows",
        autocomplete: "autocomplete"
      },
      outputs: {
        focusEvent: "focusEvent"
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => TextAreaComponent),
        multi: true
      }])],
      decls: 5,
      vars: 14,
      consts: [[1, "cxone-text-area"], ["tabindex", "0", 3, "name", "ngClass", "value", "placeholder", "autocomplete", "cols", "rows", "wrap", "input", "blur", "focus"], ["inputElementRef", ""], ["class", "max-length-text", 4, "ngIf"], [1, "max-length-text"]],
      template: function TextAreaComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵelementStart(0, "div", 0)(1, "textarea", 1, 2);
          i0.ɵɵlistener("input", function TextAreaComponent_Template_textarea_input_1_listener($event) {
            return ctx.onChanged($event);
          })("blur", function TextAreaComponent_Template_textarea_blur_1_listener() {
            return ctx.onTouched();
          })("focus", function TextAreaComponent_Template_textarea_focus_1_listener($event) {
            return ctx.focusEvent.emit($event);
          });
          i0.ɵɵtext(3, "    ");
          i0.ɵɵelementEnd();
          i0.ɵɵtemplate(4, TextAreaComponent_span_4_Template, 6, 2, "span", 3);
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          i0.ɵɵadvance();
          i0.ɵɵproperty("name", ctx.name)("ngClass", i0.ɵɵpureFunction2(11, _c1, ctx.readonly === true, ctx.resizable === false))("value", ctx.modelValue)("placeholder", ctx.placeholder)("autocomplete", ctx.autocomplete)("cols", ctx.cols ? ctx.cols : null)("rows", ctx.rows ? ctx.rows : null)("wrap", ctx.wrap);
          i0.ɵɵattribute("id", ctx.textInputId ? ctx.textInputId : null)("maxlength", ctx.maxlength ? ctx.maxlength : null);
          i0.ɵɵadvance(3);
          i0.ɵɵproperty("ngIf", ctx.maxlength);
        }
      },
      dependencies: [i1.NgClass, i1.NgIf],
      styles: ["cxone-text-area.ng-invalid.ng-touched textarea,cxone-text-area.ng-dirty.ng-invalid.ng-untouched textarea{border-color:#d92133}cxone-text-area:hover:not(.ng-invalid):not([ng-reflect-is-disabled=true]) textarea,cxone-text-area:active:not(.ng-invalid):not([ng-reflect-is-disabled=true]) textarea{border-color:#007cbe}.cxone-text-area textarea{height:58px;padding:7px 10px;width:100%;background-color:#fff;border:1px solid #B8C5CC;box-sizing:border-box;font-size:13px;font-family:OpenSans;outline:0;line-height:21px;font-weight:400;box-shadow:none}.cxone-text-area textarea:focus{border:1px solid rgb(0,124,190)}.cxone-text-area textarea::placeholder{color:#767676}.cxone-text-area textarea:disabled{background-color:#f8f8f8;border-color:#d0d2d3;color:#2e2e2e}.cxone-text-area textarea.readonly{margin:0;border:none;pointer-events:none;border-color:#d0d2d3;background-color:#f8f8f8;color:#2e2e2e;opacity:.8}.cxone-text-area textarea.readonly::placeholder{color:transparent}.cxone-text-area textarea:-webkit-autofill,.cxone-text-area textarea :-webkit-autofill:hover,.cxone-text-area textarea :-webkit-autofill:focus{background-color:#fff!important}.cxone-text-area .not-resizable{resize:none}.cxone-text-area .max-length-text{font-size:10px}\n"],
      encapsulation: 2
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TextAreaComponent, [{
    type: Component,
    args: [{
      selector: 'cxone-text-area',
      providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => TextAreaComponent),
        multi: true
      }],
      encapsulation: ViewEncapsulation.None,
      template: "<div class=\"cxone-text-area\">\n    <textarea #inputElementRef\n        [attr.id]=\"textInputId ? textInputId : null\"\n        [name]=\"name\"\n        [ngClass]=\"{ 'readonly': readonly === true, 'not-resizable': resizable === false }\"\n        [value]=\"modelValue\"\n        [placeholder]=\"placeholder\"\n        [autocomplete]=\"autocomplete\"\n        [cols]=\"cols ? cols : null\"\n        [rows]=\"rows ? rows : null\"\n        [wrap]=\"wrap\"\n        [attr.maxlength]=\"maxlength ? maxlength : null\"\n        (input)=\"onChanged($event)\"\n        tabindex=\"0\"\n        (blur)=\"onTouched()\"\n        (focus)=\"focusEvent.emit($event)\">\n    </textarea>\n    <span \n        *ngIf=\"maxlength\"\n        class=\"max-length-text\"\n    >\n        <em>{{modelValue.length}}</em> / <em>{{maxlength}}</em>\n    </span>\n</div>\n",
      styles: ["cxone-text-area.ng-invalid.ng-touched textarea,cxone-text-area.ng-dirty.ng-invalid.ng-untouched textarea{border-color:#d92133}cxone-text-area:hover:not(.ng-invalid):not([ng-reflect-is-disabled=true]) textarea,cxone-text-area:active:not(.ng-invalid):not([ng-reflect-is-disabled=true]) textarea{border-color:#007cbe}.cxone-text-area textarea{height:58px;padding:7px 10px;width:100%;background-color:#fff;border:1px solid #B8C5CC;box-sizing:border-box;font-size:13px;font-family:OpenSans;outline:0;line-height:21px;font-weight:400;box-shadow:none}.cxone-text-area textarea:focus{border:1px solid rgb(0,124,190)}.cxone-text-area textarea::placeholder{color:#767676}.cxone-text-area textarea:disabled{background-color:#f8f8f8;border-color:#d0d2d3;color:#2e2e2e}.cxone-text-area textarea.readonly{margin:0;border:none;pointer-events:none;border-color:#d0d2d3;background-color:#f8f8f8;color:#2e2e2e;opacity:.8}.cxone-text-area textarea.readonly::placeholder{color:transparent}.cxone-text-area textarea:-webkit-autofill,.cxone-text-area textarea :-webkit-autofill:hover,.cxone-text-area textarea :-webkit-autofill:focus{background-color:#fff!important}.cxone-text-area .not-resizable{resize:none}.cxone-text-area .max-length-text{font-size:10px}\n"]
    }]
  }], () => [{
    type: i0.Renderer2
  }], {
    name: [{
      type: Input
    }],
    accessLabelledby: [{
      type: Input
    }],
    accessLabel: [{
      type: Input
    }],
    placeholder: [{
      type: Input
    }],
    readonly: [{
      type: Input
    }],
    required: [{
      type: Input
    }],
    textInputId: [{
      type: Input
    }],
    resizable: [{
      type: Input
    }],
    maxlength: [{
      type: Input
    }],
    wrap: [{
      type: Input
    }],
    cols: [{
      type: Input
    }],
    rows: [{
      type: Input
    }],
    autocomplete: [{
      type: Input
    }],
    focusEvent: [{
      type: Output
    }],
    inputElementRef: [{
      type: ViewChild,
      args: ['inputElementRef', {
        static: true
      }]
    }]
  });
})();
class TextAreaModule {
  /** @nocollapse */static {
    this.ɵfac = function TextAreaModule_Factory(t) {
      return new (t || TextAreaModule)();
    };
  }
  /** @nocollapse */
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: TextAreaModule
    });
  }
  /** @nocollapse */
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule, FormsModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TextAreaModule, [{
    type: NgModule,
    args: [{
      declarations: [TextAreaComponent],
      imports: [CommonModule, FormsModule],
      exports: [TextAreaComponent]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { TextAreaComponent, TextAreaModule };
